import { AxiosError } from 'axios';
import {
  B2B_PURCHASE_HISTORY_PAGE,
  CASHSTART_RELOAD_GIFT_CARD_PAGE_URL,
  RELOAD_GIFT_CARD_PAGE_URL,
} from '../constants/links';
import { currentYear, listOfMonths } from '../constants/date';
import { TSearchSelectorValue } from '../constants/payments';
import { getHost } from './general';
import { redirectTo } from './navigation';
import {
  IPaymentCardCommonData,
  IPaymentCardMandatoryPropsForHDPass,
  IPaymentCards,
  TAddCardRequest,
  TPaymentErrorResponse,
  TPaymentFormValuesAddCard,
  TPaymentFormValuesHDPass,
} from '../models/common/payments';
import {
  OrangePayCardBrandFilterValue,
  TCardResponseMicroUi,
} from '../components/addcreditcard/credit-card-form/constants';
import { IAuthHdWalletWithDetailsRequest } from '../models/b2b/payments';
import { IMappedAddress } from '../models/common/addresses';
import { getCustomerIp } from './customer';
import { getCookie } from './cookies';

export const expirationMonthValues = listOfMonths.map((month, ind) => {
  const count = ind + 1;
  const value = count < 10 ? '0' + count : count.toString();
  return {
    label: month,
    value,
  };
});

export const expirationYearValues = new Array(20)
  .fill(currentYear)
  .map((year: number, ind) => {
    const nextYear = year + ind;
    return {
      label: nextYear.toString(),
      value: nextYear.toString(),
    };
  });

export const redirectToOrderHistoryWithSearch = (
  requestSearchSelectorValue: TSearchSelectorValue,
  requestSearchCriteria: string,
) => {
  const searchParams = new URLSearchParams({
    requestSearchSelectorValue,
    requestSearchCriteria,
  });
  redirectTo(
    `${getHost()}${B2B_PURCHASE_HISTORY_PAGE()}?${searchParams.toString()}`,
  );
};

export const getNewPageNumber = (
  currentPageNumber: number,
  totalPages: number,
  paymentsOnCurrentPage: number | undefined,
) => {
  const isLastPage = currentPageNumber > 1 && currentPageNumber === totalPages;
  const isLastPaymentOnPage = paymentsOnCurrentPage === 1;
  return isLastPaymentOnPage && isLastPage
    ? currentPageNumber - 1
    : currentPageNumber;
};

export const getFormattedKeytag = (card: IPaymentCardCommonData) => {
  const keytagNumber = card?.cardNumber;
  if (keytagNumber.length !== 16) {
    return null;
  }
  const formattedNumber =
    keytagNumber.slice(0, 4) +
    '-' +
    keytagNumber.slice(4, 8) +
    '-' +
    keytagNumber.slice(8, 12) +
    '-' +
    keytagNumber.slice(12, 16);

  return formattedNumber;
};

export const getPaymentsErrorCode = (
  error: AxiosError<TPaymentErrorResponse>,
) =>
  error?.response?.data?.paymentCards?.errors[0]?.errorCode ||
  error?.response?.data?.errors?.[0]?.errorCode;

export const getPaymentsErrorMessage = (
  error: AxiosError<TPaymentErrorResponse>,
) =>
  error?.response?.data?.paymentCards?.errors[0]?.errorMessage ||
  error?.response?.data?.errors?.[0]?.errorMessage;

export const redirectToReloadGiftCardPage = (useNewReloadUrl: boolean) => {
  redirectTo(
    useNewReloadUrl
      ? RELOAD_GIFT_CARD_PAGE_URL
      : CASHSTART_RELOAD_GIFT_CARD_PAGE_URL,
  );
};

export const hasNoPaymentCards = (
  tender: IPaymentCards | null | undefined,
): boolean => {
  if (!tender) return true;
  return tender.paymentCards.length === 0;
};

const showExpirationArray: { BRAND: string; SHOW_EXPIRATION: boolean }[] = [
  { BRAND: 'HDMC', SHOW_EXPIRATION: false },
  { BRAND: 'PROX', SHOW_EXPIRATION: false },
  { BRAND: 'HDCOM', SHOW_EXPIRATION: false },
  { BRAND: 'HDCON', SHOW_EXPIRATION: false },
  { BRAND: 'VISA', SHOW_EXPIRATION: true },
  { BRAND: 'MA', SHOW_EXPIRATION: true },
  { BRAND: 'AMEX', SHOW_EXPIRATION: true },
  { BRAND: 'DISC', SHOW_EXPIRATION: true },
  { BRAND: 'PROP', SHOW_EXPIRATION: true }, // PROPURCHASE
];

export const shouldShowCardExpirationWithBrand = (brand: string): boolean => {
  const card = showExpirationArray.find((cc) => cc.BRAND === brand);
  return card?.SHOW_EXPIRATION ?? true;
};

export const creditCardHasMandatoryFieldsForHDPassEnroll = (
  card: IPaymentCardMandatoryPropsForHDPass,
): boolean => {
  if (!card || !card.billingAddress) return false;

  const { addressId, addressLine1, postalCode, stateCode, cityName } =
    card.billingAddress;

  // Common mandatory properties
  const hasCommonMandatoryProperties =
    addressId !== '' &&
    addressLine1?.length > 0 &&
    card.cardHolderName &&
    card.cardNumberLast4.length > 0 &&
    postalCode?.length > 0 &&
    stateCode?.length > 0 &&
    cityName?.length > 0;

  // Additional mandatory properties if card brand requires expiration
  const requiresExpiration = shouldShowCardExpirationWithBrand(card.cardBrand);
  const hasExpirationProperties = requiresExpiration
    ? card.cardExpiryMonth?.length > 0 && card.cardExpiryYear?.length > 0
    : true;

  return !!hasCommonMandatoryProperties && hasExpirationProperties;
};

const ccHasMandatoryFieldsForEnrollment = (
  card: IPaymentCardMandatoryPropsForHDPass | null,
): boolean => {
  if (!card || !card.billingAddress) return false;

  const { addressId, addressLine1, postalCode, stateCode, cityName } =
    card.billingAddress;

  // Common mandatory properties
  const hasCommonMandatoryProperties =
    addressId !== '' &&
    addressLine1?.length > 0 &&
    card.cardHolderName &&
    card.cardNumberLast4.length > 0 &&
    postalCode?.length > 0 &&
    stateCode?.length > 0 &&
    cityName?.length > 0;

  // Additional mandatory properties if card brand requires expiration
  const requiresExpiration = shouldShowCardExpirationWithBrand(card.cardBrand);
  const hasExpirationProperties = requiresExpiration
    ? card.cardExpiryMonth?.length > 0 && card.cardExpiryYear?.length > 0
    : true;

  return !!hasCommonMandatoryProperties && hasExpirationProperties;
};

export const mapAddCreditCardToRequestModel = (
  formValuesAddCard: TPaymentFormValuesAddCard,
  { cardDetails, cardReference, posEntryMode }: TCardResponseMicroUi,
  sessionUuid: string,
): TAddCardRequest => {
  const {
    addressId,
    cardNickName,
    cardHolderName,
    isDefault,
    addressLine1,
    addressLine2,
    zipCode,
    cityState,
    isHDWalletFlow,
  } = formValuesAddCard;
  const {
    expirationMonth,
    expirationYear,
    reportPaymentMethodCode,
    accountNumberXref,
  } = cardDetails;
  const buyerId = cardDetails.authorizedBuyerID;
  const [cityName, stateCode] = cityState.split(', ');
  const cardBrand =
    OrangePayCardBrandFilterValue[cardDetails.reportPaymentMethodCode];
  const avoidSendExpDate =
    cardBrand === 'HDCOM' || cardBrand === 'HDCON' || Boolean(buyerId);
  const phoneNumber = formValuesAddCard.primaryPhone?.number
    ? formValuesAddCard.primaryPhone?.number.replace(/-/g, '')
    : undefined;
  return {
    cardNickName,
    cardHolderName,
    cardType: 'cc',
    cardBrand,
    isDefault,
    isActionSetAsDefault: isDefault,
    buyerId,
    customerIp: getCustomerIp(),
    forterToken: getCookie('forterToken', document.cookie) || '',
    enrollHDWallet: isHDWalletFlow ? 'Y' : 'N',
    cardExpiryMonth: avoidSendExpDate ? undefined : expirationMonth,
    cardExpiryYear: avoidSendExpDate ? undefined : expirationYear,
    xrefCardNumber: accountNumberXref,
    reportPaymentMethodCode,

    posEntryMode,
    sessionUuid,
    billingAddress: {
      stateCode,
      countryCode: 'US',
      addressLine1,
      addressLine2: addressLine2 || null,
      postalCode: zipCode,
      cityName,
      phoneNumber,
      addressId: addressId || undefined,
    },
    cardReference,
    cardDetails,
  };
};

export const mapHDWalletAddedCreditCardToRequestModel = (
  cardFormValues: TPaymentFormValuesHDPass,
  paymentId: string,
  { cardDetails, cardReference, posEntryMode }: TCardResponseMicroUi,
): IAuthHdWalletWithDetailsRequest => {
  const buyerId = cardDetails.authorizedBuyerID;
  const [cityName, stateCode] = cardFormValues.cityState.split(', ');
  const cardBrand =
    OrangePayCardBrandFilterValue[
      cardDetails.reportPaymentMethodCode
    ].toUpperCase();
  const avoidSendExpDate =
    cardBrand === 'HDCOM' || cardBrand === 'HDCON' || Boolean(buyerId);

  const {
    cardNickName,
    cardHolderName,
    addressLine1,
    addressLine2,
    zipCode,
    cardExpiryMonth,
    cardExpiryYear,
  } = cardFormValues;

  return {
    paymentCardRequest: {
      paymentCard: {
        paymentId,
        cardNickName,
        cardExpiryMonth: avoidSendExpDate ? undefined : cardExpiryMonth,
        cardExpiryYear: avoidSendExpDate ? undefined : cardExpiryYear,
        cardHolderName,
        posEntryMode,
        billingAddress: {
          stateCode,
          countryCode: 'US',
          addressLine1,
          addressLine2: addressLine2 || null,
          postalCode: zipCode,
          cityName,
        },
        customerIp: getCustomerIp(),
        forterToken: getCookie('forterToken', document.cookie) || '',
        cardDetails,
        cardReference,
      },
    },
  };
};

export const checkExpiredCard = (expiryMonth: string, expiryYear: string) => {
  const currentDate = new Date().getTime();
  const selectedDate = new Date(+expiryYear, +expiryMonth).getTime();

  return currentDate > selectedDate;
};

export const createBillingAddressObject = (
  values: TPaymentFormValuesHDPass | TPaymentFormValuesAddCard,
) => {
  const [city, state] = values.cityState.split(', ');
  return {
    addressLine1: values?.addressLine1,
    addressLine2: values?.addressLine2 ?? '',
    city,
    state,
    zipCode: values?.zipCode,
  };
};

export function hasBillingAddress(
  card: IPaymentCardMandatoryPropsForHDPass | IPaymentCardCommonData,
): card is IPaymentCardMandatoryPropsForHDPass {
  if ('billingAddress' in card) {
    return ccHasMandatoryFieldsForEnrollment(card);
  }
  return false;
}

export const getFormValuesFromAddressSelected = (
  addressSelected: IMappedAddress | null,
): Partial<TPaymentFormValuesAddCard> => {
  return {
    addressLine1: addressSelected?.addressLine1 || '',
    addressLine2: addressSelected?.addressLine2,
    cityState: addressSelected?.city + ', ' + addressSelected?.state,
    zipCode: addressSelected?.zipCode || '',
    primaryPhone: {
      number: addressSelected?.primaryPhone || '',
    },
    addressId:
      addressSelected?.addrIdentifier != null
        ? String(addressSelected?.addrIdentifier)
        : '',
  };
};
